// Live numbers (docs/ONLINE_IDE_LIVE_CODE.md, Phase B): with Live on, an edit
// that changes only numbers and strings in a live build changes them in the
// running program, without building it again.
//
// A live build lists its literals (compile.mjs, liveNumbers): where each is,
// whether it can change while the program runs, and where its value goes in
// the program's data. numberSpans finds them in the text that was built;
// numberChange compares an edit with that text.

// A literal as the compiler reads it (compiler/toker.cpp): a string in quotes,
// which holds no quote, or a number: $hex, %binary, or decimal, a Float when
// it has a point.
const NUMBER = /"[^"\n]*"|\$[0-9a-fA-F]+|%[01]+|\d+\.\d*|\.\d+|\d+/y;

const byteLength = (text) => new TextEncoder().encode(text).length;

// The value and type of a literal's text, or null when the text is not one
// literal the IDE can change: an Int too large for an Int, say.
export function parseNumber(text) {
  NUMBER.lastIndex = 0;
  const match = NUMBER.exec(text);
  if (!match || match[0].length !== text.length) return null;
  if (text[0] === '"') return { type: "string", value: text.slice(1, -1) };
  if (text[0] === "$" || text[0] === "%") {
    // As the compiler reads them, wrapping at 32 bits.
    const [shift, base] = text[0] === "$" ? [4, 16] : [1, 2];
    let value = 0;
    for (const digit of text.slice(1)) value = (value << shift) | parseInt(digit, base);
    return { type: "int", value: value | 0 };
  }
  if (text.includes(".")) return { type: "float", value: Math.fround(parseFloat(text)) };
  const value = Number(text);
  return value <= 0x7fffffff ? { type: "int", value } : null;
}

// The character offset of a byte column (from 1) in a line.
function columnOffset(line, col) {
  let bytes = 0, offset = 0;
  for (const character of line) {
    if (bytes >= col - 1) break;
    const code = character.codePointAt(0);
    bytes += code < 0x80 ? 1 : code < 0x800 ? 2 : code < 0x10000 ? 3 : 4;
    offset += character.length;
  }
  return bytes === col - 1 ? offset : -1;
}

// The live build's numbers in the text it was built from: files { path:
// text }, numbers its liveNumbers. Returns Map path -> spans [{ from, to,
// number }], in order, from and to being character offsets.
export function numberSpans(files, numbers) {
  const spans = new Map();
  const lineStarts = new Map();
  for (const number of numbers) {
    const text = files[number.file];
    if (typeof text !== "string") continue;
    if (!lineStarts.has(number.file)) {
      const starts = [0];
      for (let k = text.indexOf("\n"); k >= 0; k = text.indexOf("\n", k + 1)) starts.push(k + 1);
      lineStarts.set(number.file, starts);
    }
    const start = lineStarts.get(number.file)[number.row - 1];
    if (start === undefined) continue;
    const end = text.indexOf("\n", start);
    const column = columnOffset(text.slice(start, end < 0 ? text.length : end), number.col);
    if (column < 0) continue;
    const from = start + column;
    NUMBER.lastIndex = from;
    const match = NUMBER.exec(text);
    // The literal must be where the compiler saw it; its length is in bytes.
    if (!match || byteLength(match[0]) !== number.length) continue;
    if (!spans.has(number.file)) spans.set(number.file, []);
    spans.get(number.file).push({ from, to: from + match[0].length, number });
  }
  for (const list of spans.values()) list.sort((a, b) => a.from - b.from);
  return spans;
}

// The numbers an edit changed in one file, matching the new text against the
// old with only the numbers allowed to differ. Returns [{ span, text, from,
// to }] for every number, with its new text and place, or null when anything
// else changed.
function matchNumbers(before, after, spans) {
  const found = [];
  let p = 0, q = 0;
  for (const span of spans) {
    const between = before.slice(p, span.from);
    if (!after.startsWith(between, q)) return null;
    q += between.length;
    NUMBER.lastIndex = q;
    const match = NUMBER.exec(after);
    if (!match) return null;
    found.push({ span, text: match[0], from: q, to: q + match[0].length });
    p = span.to;
    q += match[0].length;
  }
  return before.slice(p) === after.slice(q) ? found : null;
}

// Compares the files of an edit, after, with those a live build was built
// from, before, whose numbers are spans (numberSpans). Returns null when the
// edit changed more than numbers. Otherwise returns { changed, spans,
// writes, reason }: changed the numbers that changed, [{ path, from, to,
// before, after }]; spans the numbers' places in the edited text; and either
// writes [{ offset, type, value }] that make the running program match, or
// the reason it must be built again.
export function numberChange(before, after, spans) {
  const paths = new Set([...Object.keys(before), ...Object.keys(after)]);
  const changed = [], nextSpans = new Map(spans);
  for (const path of paths) {
    if (before[path] === after[path]) continue;
    if (typeof before[path] !== "string" || typeof after[path] !== "string" || !spans.has(path)) return null;
    const found = matchNumbers(before[path], after[path], spans.get(path));
    if (!found) return null;
    nextSpans.set(path, found.map(({ span, from, to }) => ({ from, to, number: span.number })));
    for (const { span, text, from, to } of found) {
      const old = before[path].slice(span.from, span.to);
      if (text !== old) changed.push({ path, from, to, before: old, after: text, number: span.number });
    }
  }
  if (!changed.length) return null;
  const writes = [];
  let reason = null;
  for (const { before: old, after: text, number } of changed) {
    const parsed = parseNumber(text);
    if (!parsed) {
      reason = `${text} is not a value the program can take while it runs.`;
    } else if (parsed.type !== number.type) {
      reason = `${old} became ${text}, which changes its type.`;
    } else if (!number.live) {
      // A number the compiler used, such as a Dim's size or a Step; one that
      // code before the main loop used, which has run; or one in code the
      // program does not use.
      reason = number.fixed ? `${old} is fixed when the program starts, as a Dim's size or a Step is.` :
        number.setup ? `${old} is used before the main loop, which has already run.` : "";
    } else {
      for (const target of number.targets) {
        let value = parsed.value;
        if (target.type !== "string") {
          if (target.negate) value = -value;
          value = target.type === "float" ? Math.fround(value) : value | 0;
        }
        writes.push({ offset: target.offset, type: target.type, value });
      }
      continue;
    }
    break;
  }
  return { changed, spans: nextSpans, writes: reason === null ? writes : [], reason };
}
